import React, { useContext, useState } from 'react';
import { Dialog, DialogContent, DialogTitle } from '@mui/material';
import { enqueueSnackbar } from 'notistack';
import { GlobalContext } from '../../../providers/GlobalProvider';
import { useCreateBulkEntities } from '../../../api/bitedb/entities/entities.hooks';
import { DuplicateEntity } from 'api/bitedb/entities/entities';
import { SingleEntityForm } from './SingleEntityForm';
import { QuickAddEntities, QuickAddEntity } from './QuickAddEntities';

type AddEntityTab = 'single' | 'multiple';

interface IAddEntityModalProps {
  open: boolean;
  handleClose: () => void;
  title?: string;
  database: any;
  handleRefresh?: any;
  onEntityAdded?: () => void;
  onImportCsv?: () => void;
  onDuplicatesDetected?: (payload: {
    duplicates: DuplicateEntity[];
    fallbackEntities: any[];
  }) => void;
}

const tabBaseStyle: React.CSSProperties = {
  padding: '8px 16px',
  borderRadius: '8px',
  fontFamily: 'Inter',
  fontSize: '14px',
  fontWeight: 600,
  cursor: 'pointer',
  border: 'none',
  background: 'transparent',
  color: 'var(--text-secondary)',
};

const tabActiveStyle: React.CSSProperties = {
  background: 'var(--bg-primary)',
  color: 'var(--text-primary)',
  boxShadow: '0px 1px 2px 0px rgba(16, 24, 40, 0.05)',
};

export const AddEntityModal: React.FC<IAddEntityModalProps> = ({
  open,
  handleClose,
  title = 'Add Entity',
  database,
  handleRefresh,
  onEntityAdded,
  onImportCsv,
  onDuplicatesDetected,
}) => {
  const { cur_user } = useContext(GlobalContext);
  const [activeTab, setActiveTab] = useState<AddEntityTab>('multiple');
  // Uses the duplicate-checking bulk route (same behaviour as CSV upload):
  // non-duplicates are inserted, and entities already in the database come back
  // as `duplicates` for review instead of being silently added.
  const createBulkMutation = useCreateBulkEntities();

  const handleSaveAll = (entities: QuickAddEntity[]) => {
    if (createBulkMutation.isPending) {
      return;
    }
    if (!database?.id) {
      enqueueSnackbar('Database information missing', { variant: 'error' });
      return;
    }

    const now = new Date().toISOString();
    const payload = entities.map((entity) => ({
      name: entity.name,
      entity_type: entity.entity_type || 'company',
      country_of_origin: entity.country || undefined,
      address: entity.address || undefined,
      risk_level: 'Low',
      created_at: now,
      updated_at: now,
      database_id: database.id,
      created_by: cur_user?.basics?.id,
    }));

    createBulkMutation.mutate(
      {
        entities: payload,
        databaseId: database.id,
        createdBy: cur_user?.basics?.id || '',
        teamId: cur_user?.selected_team?.id || '',
      },
      {
        onSuccess: (res) => {
          const duplicates = Array.isArray(res?.duplicates)
            ? res.duplicates
            : [];
          const insertedCount = Array.isArray(res?.data) ? res.data.length : 0;

          if (insertedCount > 0) {
            enqueueSnackbar(
              `${insertedCount} entit${
                insertedCount > 1 ? 'ies' : 'y'
              } added successfully.`,
              { variant: 'success' },
            );
            handleRefresh?.();
            onEntityAdded?.();
          }

          // Existing-database duplicates go to the review flow (like CSV upload),
          // which also closes this modal.
          if (duplicates.length > 0 && onDuplicatesDetected) {
            enqueueSnackbar(
              `${duplicates.length} potential duplicate${
                duplicates.length > 1 ? 's' : ''
              } need review.`,
              { variant: 'warning' },
            );
            onDuplicatesDetected({
              duplicates: duplicates as any,
              fallbackEntities: payload,
            });
            return;
          }

          handleClose();
        },
        onError: (error: any) => {
          const errorMessage =
            error?.response?.data?.message ||
            error?.message ||
            'Failed to add entities';
          enqueueSnackbar(errorMessage, { variant: 'error' });
        },
      },
    );
  };

  return (
    <Dialog
      className='dialog_box'
      open={open}
      onClose={handleClose}
      maxWidth='lg'
      fullWidth
      PaperProps={{
        style: {
          backgroundColor: 'var(--bg-colors)',
          boxShadow:
            '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
          borderRadius: '0.5rem',
          overflow: 'hidden',
          maxWidth: '1024px',
          width: '92%',
          maxHeight: '90vh',
          margin: '0 auto',
          display: 'flex',
          flexDirection: 'column',
        },
      }}
    >
      <DialogTitle
        id='add-entity-modal-title'
        className='modal-header-content modal-title'
      >
        {title}
        <div
          className='audit-add-modal-head-cont-icon'
          onClick={handleClose}
          style={{ cursor: 'pointer' }}
        >
          <svg
            xmlns='http://www.w3.org/2000/svg'
            width='18'
            height='18'
            viewBox='0 0 18 18'
            fill='none'
          >
            <path
              d='M1.5 1.5L16.5 16.5M16.5 1.5L1.5 16.5'
              stroke='var(--text-primary)'
              strokeWidth='1.5'
              strokeLinecap='round'
              strokeLinejoin='round'
            />
          </svg>
        </div>
      </DialogTitle>
      <DialogContent
        className='dialog-content-base'
        style={{
          display: 'flex',
          flexDirection: 'column',
          flex: 1,
          minHeight: 0,
          overflow: 'hidden',
          padding: 0,
        }}
      >
        <p
          style={{
            color: 'var(--text-secondary)',
            fontFamily: 'Inter',
            fontSize: '14px',
            margin: '16px 24px 0',
            flexShrink: 0,
          }}
        >
          Quickly add one or many entities to {database?.name || 'this'}{' '}
          database.
        </p>

        {/* Tab toggle (pinned) */}
        <div style={{ padding: '16px 24px 0', flexShrink: 0 }}>
          <div
            role='tablist'
            style={{
              display: 'inline-flex',
              gap: '4px',
              padding: '4px',
              borderRadius: '10px',
              background: 'var(--bg-secondary)',
            }}
          >
            <button
              type='button'
              role='tab'
              aria-selected={activeTab === 'single'}
              onClick={() => setActiveTab('single')}
              style={{
                ...tabBaseStyle,
                ...(activeTab === 'single' ? tabActiveStyle : {}),
              }}
            >
              Single entity
            </button>
            <button
              type='button'
              role='tab'
              aria-selected={activeTab === 'multiple'}
              onClick={() => setActiveTab('multiple')}
              style={{
                ...tabBaseStyle,
                ...(activeTab === 'multiple' ? tabActiveStyle : {}),
              }}
            >
              Quick add multiple
            </button>
          </div>
        </div>

        {/* Form region fills the remaining height; each form scrolls its own
            fields and pins its footer. */}
        <div style={{ flex: 1, minHeight: 0, display: 'flex' }}>
          {activeTab === 'single' ? (
            <div style={{ flex: 1, minHeight: 0 }}>
              <SingleEntityForm
                database={database}
                handleClose={handleClose}
                handleRefresh={handleRefresh}
                onEntityAdded={onEntityAdded}
                onDuplicatesDetected={onDuplicatesDetected}
              />
            </div>
          ) : (
            <div style={{ flex: 1, minHeight: 0 }}>
              <QuickAddEntities
                onSaveAll={handleSaveAll}
                onImportCsv={
                  onImportCsv
                    ? () => {
                        handleClose();
                        onImportCsv();
                      }
                    : undefined
                }
                isSaving={createBulkMutation.isPending}
              />
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default AddEntityModal;
